import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { FolderKanban } from "lucide-react";

export default function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/dashboard" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/dashboard");
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row">
            <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2 mb-12">
                        <div className="w-8 h-8 bg-[#09090b] rounded-sm flex items-center justify-center">
                            <FolderKanban className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <div className="font-display text-base leading-none tracking-tighter">
                                Social Factory
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                AI Portal
                            </div>
                        </div>
                    </div>

                    <h1 className="font-display text-4xl tracking-tighter font-black mb-2">Inloggen</h1>
                    <p className="text-sm text-zinc-500 mb-8">
                        Welkom terug. Log in om je bestanden te beheren.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                E-mail
                            </label>
                            <Input
                                type="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email"
                                placeholder="jij@bedrijf.be"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                Wachtwoord
                            </label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                            data-testid="login-submit"
                        >
                            {loading ? "Inloggen…" : "Inloggen"}
                        </Button>
                    </form>

                <div className="text-sm text-zinc-500 mt-6 text-left">
                    Geen account?{" "}
                    <span className="text-zinc-700">Vraag je beheerder een account aan te maken.</span>
                </div>
                </div>
            </div>

            <div className="hidden lg:flex flex-1 bg-[#09090b] text-white p-12 items-end relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1668097613572-40b7c11c8727?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/60 to-transparent" />
                <div className="relative z-10 max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-4">
                        AI BESTANDSPORTAL
                    </div>
                    <h2 className="font-display text-5xl leading-none tracking-tighter font-black mb-6">
                        Vind elk bestand met taal.
                    </h2>
                    <p className="text-zinc-300 text-base leading-relaxed">
                        Foto's, video's, PDF's & meer. Automatisch beschreven met AI.
                        Zoek bestanden op beschrijving en vind exact wat je nodig hebt.
                    </p>
                </div>
            </div>
        </div>
    );
}
